/**
 * Spot-to-Spot Arbitrage Calculation Utilities
 *
 * Calculates arbitrage opportunities between different spot exchanges
 * considering orderbook depth, fees, and currency conversion rates.
 */

import type { OrderbookResponse } from '../types/orderbook';
import { FeeStructure } from '../models/FeeStructure';
import { EXCHANGES } from '../types/opportunity';

/**
 * Withdrawal fees by exchange (as percentage of volume)
 * These are approximate average fees - actual fees may vary by asset
 */
const WITHDRAWAL_FEES: Record<string, number> = {
  'Upbit': 0.0005,    // ~0.05% average withdrawal fee
  'Bithumb': 0.0005,  // ~0.05% average withdrawal fee
  'Binance': 0.0001,  // ~0.01% average withdrawal fee (lower for USDT networks)
};

export interface SpotArbitrageOpportunity {
  // Exchange information
  buyExchange: string;
  buyExchangeIndex: number;
  sellExchange: string;
  sellExchangeIndex: number;

  // Direction description
  direction: string; // e.g., "Upbit → Bithumb"

  // Price information (all in USDT for consistent comparison)
  buyPriceUsdt: number;  // Buy price converted to USDT
  sellPriceUsdt: number; // Sell price converted to USDT

  // Original prices in native quote currency
  buyPriceNative: number;
  sellPriceNative: number;
  buyQuoteCurrency: 'K' | 'U';
  sellQuoteCurrency: 'K' | 'U';

  // Volume and profit calculation
  budgetUsdt: number;              // Budget in USDT
  maxExecutableVolume: number;     // Max volume in base currency (e.g., BTC)
  totalBuyCostUsdt: number;        // Total cost to buy in USDT
  totalSellRevenueUsdt: number;    // Total revenue from sell in USDT

  // Fees breakdown
  buyFeeRate: number;              // Buy trading fee as decimal (e.g., 0.001 = 0.1%)
  sellFeeRate: number;             // Sell trading fee as decimal
  withdrawalFeeRate: number;       // Withdrawal fee as decimal (% of volume)
  buyFeeAmountUsdt: number;        // Buy trading fee in USDT
  sellFeeAmountUsdt: number;       // Sell trading fee in USDT
  withdrawalFeeAmountUsdt: number; // Withdrawal fee in USDT
  totalFeesUsdt: number;           // Total fees in USDT (buy + sell + withdrawal)

  // Profit
  grossProfitUsdt: number;         // Profit before fees in USDT
  netProfitUsdt: number;           // Profit after all fees in USDT
  profitPercentage: number;        // Net profit as percentage of buy cost

  // Status
  isProfitable: boolean;           // True if net profit > 0
}

/**
 * Calculate spot-to-spot arbitrage opportunity between two exchanges
 *
 * @param buyExchangeName - Name of the buy exchange
 * @param buyExchangeIndex - Index of the buy exchange in EXCHANGES array
 * @param buyOrderbook - Orderbook from buy exchange
 * @param buyQuoteCurrency - Quote currency of buy exchange ('K' or 'U')
 * @param sellExchangeName - Name of the sell exchange
 * @param sellExchangeIndex - Index of the sell exchange in EXCHANGES array
 * @param sellOrderbook - Orderbook from sell exchange
 * @param sellQuoteCurrency - Quote currency of sell exchange ('K' or 'U')
 * @param budgetUsdt - Budget in USDT
 * @param krwToUsdtRate - KRW/USDT conversion rate (e.g., 1535 means 1 USDT = 1535 KRW)
 * @returns Arbitrage opportunity details or null if not calculable
 */
export function calculateSpotArbitrageOpportunity(
  buyExchangeName: string,
  buyExchangeIndex: number,
  buyOrderbook: OrderbookResponse | null,
  buyQuoteCurrency: 'K' | 'U',
  sellExchangeName: string,
  sellExchangeIndex: number,
  sellOrderbook: OrderbookResponse | null,
  sellQuoteCurrency: 'K' | 'U',
  budgetUsdt: number,
  krwToUsdtRate: number | null
): SpotArbitrageOpportunity | null {
  // Validate inputs
  if (!buyOrderbook || !sellOrderbook || budgetUsdt <= 0) {
    return null;
  }

  // Check if orderbooks have sufficient levels
  if (buyOrderbook.asks.length === 0 || sellOrderbook.bids.length === 0) {
    return null;
  }

  // Get best prices
  const buyPriceNative = buyOrderbook.asks[0].price; // Best ask (where we buy)
  const sellPriceNative = sellOrderbook.bids[0].price; // Best bid (where we sell)

  // Convert prices to USDT for comparison
  let buyPriceUsdt = buyPriceNative;
  let sellPriceUsdt = sellPriceNative;

  if (buyQuoteCurrency === 'K' && krwToUsdtRate !== null) {
    buyPriceUsdt = buyPriceNative / krwToUsdtRate;
  }

  if (sellQuoteCurrency === 'K' && krwToUsdtRate !== null) {
    sellPriceUsdt = sellPriceNative / krwToUsdtRate;
  }

  // Get fee rates
  const buyQuote = buyQuoteCurrency === 'K' ? 'KRW' : 'USDT';
  const sellQuote = sellQuoteCurrency === 'K' ? 'KRW' : 'USDT';
  const buyFeeRate = FeeStructure.getFee(buyExchangeName, 'taker', buyQuote);
  const sellFeeRate = FeeStructure.getFee(sellExchangeName, 'maker', sellQuote);

  // Calculate how much volume we can buy with the budget
  let remainingBudgetUsdt = budgetUsdt;
  let volumeBeforeFees = 0;
  let totalBuyCostNative = 0;

  // Convert budget to buy quote currency
  let budgetInBuyQuote = budgetUsdt;
  if (buyQuoteCurrency === 'K' && krwToUsdtRate !== null) {
    budgetInBuyQuote = budgetUsdt * krwToUsdtRate;
  }

  // Consume buy orderbook levels until budget is exhausted
  for (const level of buyOrderbook.asks) {
    const costAtLevelNative = level.price * level.volume;
    const costAtLevelUsdt = buyQuoteCurrency === 'K' && krwToUsdtRate !== null
      ? costAtLevelNative / krwToUsdtRate
      : costAtLevelNative;

    if (costAtLevelUsdt <= remainingBudgetUsdt) {
      // Can afford entire level
      volumeBeforeFees += level.volume;
      totalBuyCostNative += costAtLevelNative;
      remainingBudgetUsdt -= costAtLevelUsdt;
    } else {
      // Partial level - calculate how much we can afford
      const volumeAtLevel = remainingBudgetUsdt / (buyQuoteCurrency === 'K' && krwToUsdtRate !== null
        ? level.price / krwToUsdtRate
        : level.price);
      volumeBeforeFees += volumeAtLevel;
      totalBuyCostNative += level.price * volumeAtLevel;
      remainingBudgetUsdt = 0;
      break;
    }

    if (remainingBudgetUsdt <= 0) break;
  }

  // Get withdrawal fee
  const withdrawalFeeRate = WITHDRAWAL_FEES[buyExchangeName] || 0.0005; // Default 0.05%

  // Apply buy fee and withdrawal fee to get executable volume
  const maxExecutableVolume = volumeBeforeFees * (1 - buyFeeRate - withdrawalFeeRate);

  // Calculate sell revenue by consuming sell orderbook levels
  let remainingVolume = maxExecutableVolume;
  let totalSellRevenueNative = 0;

  for (const level of sellOrderbook.bids) {
    const volumeAtLevel = Math.min(remainingVolume, level.volume);
    if (volumeAtLevel <= 0) break;

    totalSellRevenueNative += level.price * volumeAtLevel;
    remainingVolume -= volumeAtLevel;
  }

  // Apply sell fee
  const totalSellRevenueAfterFee = totalSellRevenueNative * (1 - sellFeeRate);

  // Convert to USDT for final comparison
  const totalBuyCostUsdt = buyQuoteCurrency === 'K' && krwToUsdtRate !== null
    ? totalBuyCostNative / krwToUsdtRate
    : totalBuyCostNative;

  const totalSellRevenueUsdt = sellQuoteCurrency === 'K' && krwToUsdtRate !== null
    ? totalSellRevenueAfterFee / krwToUsdtRate
    : totalSellRevenueAfterFee;

  // Calculate fees in USDT
  const buyFeeAmountUsdt = totalBuyCostUsdt * buyFeeRate;
  const sellFeeAmountUsdt = (sellQuoteCurrency === 'K' && krwToUsdtRate !== null
    ? totalSellRevenueNative / krwToUsdtRate
    : totalSellRevenueNative) * sellFeeRate;

  // Calculate withdrawal fee in USDT (based on the volume being withdrawn)
  const withdrawalFeeAmountUsdt = totalBuyCostUsdt * withdrawalFeeRate;

  const totalFeesUsdt = buyFeeAmountUsdt + sellFeeAmountUsdt + withdrawalFeeAmountUsdt;

  // Calculate profit
  const grossProfitUsdt = totalSellRevenueUsdt + sellFeeAmountUsdt - totalBuyCostUsdt;
  const netProfitUsdt = totalSellRevenueUsdt - totalBuyCostUsdt - withdrawalFeeAmountUsdt;
  const profitPercentage = totalBuyCostUsdt > 0
    ? (netProfitUsdt / totalBuyCostUsdt) * 100
    : 0;

  return {
    buyExchange: buyExchangeName,
    buyExchangeIndex,
    sellExchange: sellExchangeName,
    sellExchangeIndex,
    direction: `${buyExchangeName} → ${sellExchangeName}`,
    buyPriceUsdt,
    sellPriceUsdt,
    buyPriceNative,
    sellPriceNative,
    buyQuoteCurrency,
    sellQuoteCurrency,
    budgetUsdt,
    maxExecutableVolume,
    totalBuyCostUsdt,
    totalSellRevenueUsdt,
    buyFeeRate,
    sellFeeRate,
    withdrawalFeeRate,
    buyFeeAmountUsdt,
    sellFeeAmountUsdt,
    withdrawalFeeAmountUsdt,
    totalFeesUsdt,
    grossProfitUsdt,
    netProfitUsdt,
    profitPercentage,
    isProfitable: netProfitUsdt > 0,
  };
}

/**
 * Calculate all possible spot-to-spot arbitrage opportunities
 * between available exchanges
 *
 * @param exchanges - Array of exchange data { name, index, orderbook, quoteCurrency }
 * @param budgetUsdt - Budget in USDT
 * @param krwToUsdtRate - KRW/USDT conversion rate
 * @returns Array of arbitrage opportunities sorted by profitability (descending)
 */
export function calculateAllSpotArbitrageOpportunities(
  exchanges: Array<{
    name: string;
    index: number;
    orderbook: OrderbookResponse | null;
    quoteCurrency: 'K' | 'U';
  }>,
  budgetUsdt: number,
  krwToUsdtRate: number | null
): SpotArbitrageOpportunity[] {
  const opportunities: SpotArbitrageOpportunity[] = [];

  // Calculate opportunities for all exchange pairs
  for (let i = 0; i < exchanges.length; i++) {
    for (let j = 0; j < exchanges.length; j++) {
      if (i === j) continue; // Skip same exchange

      const buyExchange = exchanges[i];
      const sellExchange = exchanges[j];

      // Skip if either orderbook is unavailable
      if (!buyExchange.orderbook || !sellExchange.orderbook) continue;

      const opportunity = calculateSpotArbitrageOpportunity(
        buyExchange.name,
        buyExchange.index,
        buyExchange.orderbook,
        buyExchange.quoteCurrency,
        sellExchange.name,
        sellExchange.index,
        sellExchange.orderbook,
        sellExchange.quoteCurrency,
        budgetUsdt,
        krwToUsdtRate
      );

      if (opportunity) {
        opportunities.push(opportunity);
      }
    }
  }

  // Sort by net profit (descending)
  opportunities.sort((a, b) => b.netProfitUsdt - a.netProfitUsdt);

  return opportunities;
}

/**
 * Format currency value with appropriate decimal places
 */
export function formatCurrency(value: number, decimals: number = 2): string {
  return `$${Math.abs(value).toFixed(decimals)}`;
}

/**
 * Format percentage value
 */
export function formatPercentage(value: number, decimals: number = 2): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(decimals)}%`;
}

/**
 * Get color for profit/loss display
 */
export function getProfitColor(profitPercentage: number): 'success' | 'warning' | 'error' {
  if (profitPercentage > 0) return 'success';     // >0% = profitable (green)
  if (profitPercentage === 0) return 'warning';   // 0% = break-even (yellow)
  return 'error';                                  // <0% = unprofitable (red)
}
